import type {
  AgentStreamEvent,
  ChatAgentChatInput,
  LiveTurnState,
  QueueMode,
} from '@erp/rpc';
import type { ModuleKey } from '@erp/rpc';
import { rpcClient } from 'src/api/rpc/client';
import { sharesThread } from './sharesThread';

/**
 * The chat inbox on the client (AI-518). A message sent while Nami is working
 * is queued on the server instead of stopping the turn; the turn places it
 * later (a steer after its current tool round, a follow-up once it ends) and
 * the stream that follows the turn reports it with `submission-placed`.
 */

export interface QueuedMessage {
  requestId: string;
  text: string;
  mode: QueueMode;
}

/**
 * Whether a message sent now from `moduleKey` queues behind the running
 * reply: one is streaming, its thread is known, and `moduleKey` continues
 * that thread. One rule for the composers and `sendMessage`.
 */
export function queueAllowed(state: {
  isLoading: boolean;
  threadId: string | null;
  currentModuleKey: ModuleKey | null;
  moduleKey: ModuleKey;
}): boolean {
  return (
    state.isLoading &&
    state.threadId !== null &&
    state.currentModuleKey !== null &&
    sharesThread(state.currentModuleKey, state.moduleKey)
  );
}

type SubmissionEvent = Extract<AgentStreamEvent, { type: 'submission' }>;

/** The queue a live snapshot reports, as the composer shows it. */
export function queuedFromTurn(turn: LiveTurnState): QueuedMessage[] {
  return (turn.queued ?? []).map((q) => ({
    requestId: q.requestId,
    text: q.text,
    mode: q.mode,
  }));
}

/**
 * Sends a message to a thread whose turn is running. Resolves with how the
 * server admitted it; the stream is closed right after, since the running
 * turn (or, if it just ended, a new one) carries on without it.
 */
/** The server refused a message; `code` says why (e.g. `TOO_MANY_TURNS`). */
export class SubmissionRefusedError extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
  ) {
    super(message);
    this.name = 'SubmissionRefusedError';
  }
}

export async function submitToBusyThread(
  input: ChatAgentChatInput,
): Promise<SubmissionEvent> {
  const stream = await rpcClient.ai.chatAgent.chat(input);
  for await (const event of stream) {
    if (event.type === 'submission') return event;
    if (event.type === 'error') {
      throw new SubmissionRefusedError(event.message, event.code);
    }
  }
  throw new Error('The message was not accepted');
}

export function withdrawOnServer(threadId: string, requestId: string): void {
  rpcClient.ai.chatAgent
    .withdrawSubmission({ threadId, requestId })
    .catch((err: unknown) => {
      console.warn('withdrawSubmission failed', err);
    });
}
